import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { AlertCircle, FileQuestion } from 'lucide-react'
import { clearCourseCache, loadCourseData } from '@/lib/services/courses'
import { toast } from '@/components/ui/use-toast'
import { Card, CardContent } from '@/components/ui/card'
import { useState } from 'react'

const menuItems = [
  { id: 'manage-learners', label: 'Manage Users', path: 'manageusers' },
  { id: 'enroll-learners', label: 'Enroll Users', path: 'enroll' },
  { id: 'subscriptions', label: 'Subscriptions', path: 'subscriptions' },
  { id: 'payment-methods', label: 'Payment Methods', path: 'payment-methods' },
  { id: 'workbooks', label: 'Course Materials', path: 'workbooks' },
  { id: 'password', label: 'Manage Password', path: 'password' },
  // { id: 'invoices', label: 'Invoices', path: 'invoices' },
]

const adminOnlyIds = ['manage-learners', 'enroll-learners', 'workbooks']
const billingOnlyIds = ['subscriptions', 'payment-methods']

const Admin = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { currentUser } = useAuth()
  const [showAlert, setShowAlert] = useState(false)

  const currentPath = location.pathname.split('/').pop()

  const filteredMenuItems = menuItems.filter((item) => {
    if (billingOnlyIds.includes(item.id)) {
      return currentUser?.user_level < 6
    }
    if (!adminOnlyIds.includes(item.id)) {
      return true
    } else if (currentUser?.is_individual) {
      return false
    } else if (currentUser?.user_level > 6) {
      return item.id === 'enroll-learners'
    } else if (currentUser?.user_level == 6) {
      return item.id === 'workbooks'
    } else {
      return true
    }
  })

  const allMenuItems = filteredMenuItems

  const isNothingSelected = !allMenuItems.some(
    (item) => item.path === currentPath
  )

  const clearCache = async () => {
    const success = await clearCourseCache()
    if (success) {
      toast({
        title: 'Course cache cleared successfully',
        description: 'The course cache has been cleared.',
        duration: 5000,
      })
      loadCourseData()
    } else {
      toast({
        title: 'Error clearing course cache',
        description:
          'There was an error while trying to clear the course cache.',
        variant: 'destructive',
        duration: 5000,
      })
    }
  }

  return (
    <div className="flex flex-col mt-6 px-2 lg:container">
      <div className="flex flex-col md:flex-row lg:container gap-3 items-start mt-3 mb-6 ">
        <nav className="md:w-56 w-full border rounded-md border-gray-300 p-4 bg-white shadow-sm">
          <ul className="space-y-2">
            {filteredMenuItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => navigate(item.path)}
                  className={`w-full text-left p-1.5 px-4 rounded font-semibold border-2 transition-colors ${
                    currentPath === item.path
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-900 border-primary hover:bg-gray-100'
                  }`}
                >
                  {currentUser.user_level == 6 && item.label == "Enroll Users" ? "Self Enroll" : item.label}
                </button>
              </li>
            ))}
            {currentUser?.user_level.toString() === '3' && (
              <li>
                <button
                  onClick={clearCache}
                  className="w-full text-left p-2 pl-4 rounded font-semibold border-2 transition-colors bg-white text-gray-900 border-primary hover:bg-gray-100"
                >
                  Clear Course Cache
                </button>
              </li>
            )}
          </ul>
        </nav>

        <div className="lg:max-w-[calc(100%-13rem)] flex-1 border rounded border-gray-300 p-5 w-full min-h-[300px] bg-white shadow-sm">
          {isNothingSelected ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
              <FileQuestion className="w-12 h-12 text-gray-400 mb-2" />
              <p className="text-lg md:text-xl font-semibold">
                No section selected
              </p>
              <p className="text-sm md:text-base text-gray-400 mt-1">
                Please choose an option from the menu.
              </p>
            </div>
          ) : (
            <Outlet context={{ setShowAlert }} />
          )}
        </div>
      </div>
    </div>
  )
}

export default Admin